import React, { Suspense, useCallback, useRef, useLayoutEffect, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Pause, Play } from "lucide-react";
import Layout from "../components/ui/Layout";
import SocialLinks from "../components/ui/SocialLinks";
import FadeIn from "../components/ui/FadeIn";
import SplashCursor from "../components/ui/SplashCursor";
import GhostType from "../components/ui/GhostType";
import { preloadGlanceTiles } from "../utils/media";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGlanceGallery } from "../hooks/useGlanceGallery";
import useGlobalAnimationPaused from "../hooks/useGlobalAnimationPaused";
import { useAgentDock } from "../context/AgentDockContext";
import { WRITINGS } from "../data/writings";
import {
  IC_PERMISSION_COVER,
  IC_DESIGN_SYSTEM_COVER,
  HSP_PLANET_COVER,
  COLLABSTR_DESIGN_SYSTEM_COVER,
  isVideoMedia,
} from "../data/caseStudyMedia";
import "./Portfolio.css";

const DomeGallery = React.lazy(() => import("../components/ui/DomeGallery"));

gsap.registerPlugin(ScrollTrigger);

const WORK = [
  {
    title:
      "Making complex access control look simple for 500+ security-heavy orgs",
    tags: ["B2B", "System thinking", "Responsive design"],
    image: IC_PERMISSION_COVER,
    href: "/intranetconnections",
  },
  {
    title:
      "Building the system that let a small team move like a big one, built to replicate and iterate",
    tags: ["Marketplace", "Design system", "SOPs"],
    image: COLLABSTR_DESIGN_SYSTEM_COVER,
    href: "/Collabstr",
  },
  {
    title: "A themeable design system that cut UI defects from 10 to 3 per page",
    tags: ["Design system", "B2B"],
    image: IC_DESIGN_SYSTEM_COVER,
    href: "/DesignSystem",
  },
  {
    title: "High sensitivity is a talent: a community product for highly sensitive people",
    tags: ["Community", "Wellness", "Mobile"],
    image: HSP_PLANET_COVER,
    href: "/HSPPlanet",
  },
];

const TAO_TEXT =
  "The Tao gives birth to One. One gives birth to Two. Two gives birth to Three. Three gives birth to all things. ";

function taoZone(x, y, R) {
  if (x * x + y * y > R * R) return "out";
  const h = R * 0.5;
  const eye2 = (R * 0.12) * (R * 0.12);
  const top = x * x + (y + h) * (y + h);
  const bot = x * x + (y - h) * (y - h);
  if (top <= eye2) return "yin";
  if (bot <= eye2) return "yang";
  if (top <= h * h || (x < 0 && bot > h * h)) return "yang";
  return "yin";
}

const SPIN_MS = 1900;
const FLOOD_MS = 1300;
const LOADER_MS = SPIN_MS + FLOOD_MS;
const INTRO_SEEN_KEY = "young-intro-seen";

function AsciiTaiji() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let width = 0;
    let height = 0;
    let raf = 0;
    let angle = 0;
    let last = performance.now();
    const start = last;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const easeIn = (t) => t * t * t;
    const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

    const draw = (now) => {
      const dt = Math.min(48, now - last);
      last = now;
      const t = now - start;
      const spinT = Math.min(1, t / SPIN_MS);
      const floodT = Math.max(0, Math.min(1, (t - SPIN_MS) / FLOOD_MS));

      const speed = reduced ? 0.0012 : 0.0006 + easeIn(Math.min(1, t / (SPIN_MS + FLOOD_MS * 0.6))) * 0.03;
      angle += speed * dt;

      const cx = width / 2;
      const cy = height / 2;
      const base = Math.min(width, height) * 0.16;
      const diag = Math.hypot(width, height);
      const grow = easeInOut(floodT);
      const R = base * (1 + spinT * 0.08) + grow * diag * 0.35;

      ctx.fillStyle = "#e7e7e7";
      ctx.fillRect(0, 0, width, height);

      const tw = 6.6;
      const th = 10;
      const cols = Math.ceil(width / tw);
      const rows = Math.ceil(height / th);
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      ctx.font = "500 10px ui-monospace, SFMono-Regular, Menlo, monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      const x0 = Math.max(0, Math.floor((cx - R - 4) / tw));
      const x1 = Math.min(cols, Math.ceil((cx + R + 4) / tw));
      const y0 = Math.max(0, Math.floor((cy - R - 4) / th));
      const y1 = Math.min(rows, Math.ceil((cy + R + 4) / th));
      for (let gy = y0; gy < y1; gy += 1) {
        for (let gx = x0; gx < x1; gx += 1) {
          const sx = gx * tw + tw / 2;
          const sy = gy * th + th / 2;
          const px = sx - cx;
          const py = sy - cy;
          const zone = taoZone(px * cos + py * sin, -px * sin + py * cos, R);
          if (zone === "out") continue;
          const ch = TAO_TEXT[(gy * cols + gx) % TAO_TEXT.length];
          if (zone === "yin") {
            ctx.fillStyle = "rgba(8,8,8,0.95)";
            ctx.fillText(ch, sx, sy);
            ctx.fillText(ch, sx + 0.6, sy + 0.4);
          } else {
            ctx.fillStyle = "rgba(28,28,28,0.32)";
            ctx.fillText(ch, sx, sy);
          }
        }
      }

      if (floodT > 0) {
        const inkR = easeIn(floodT) * diag * 0.62;
        ctx.fillStyle = "#050505";
        ctx.beginPath();
        const steps = 96;
        for (let i = 0; i <= steps; i += 1) {
          const a = (i / steps) * Math.PI * 2;
          const wobble =
            Math.sin(a * 3 + t * 0.004) * 0.08 +
            Math.sin(a * 5 - t * 0.006) * 0.05 +
            Math.sin(a * 9 + t * 0.009) * 0.025;
          const r = inkR * (1 + wobble * (1 - floodT * 0.6));
          const x = cx + Math.cos(a + angle * 0.3) * r;
          const y = cy + Math.sin(a + angle * 0.3) * r;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.fill();
      }

      raf = requestAnimationFrame(draw);
    };

    resize();
    raf = requestAnimationFrame(draw);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="home-loader__yy" />;
}

function WorkRowCard({ item }) {
  const videoRef = useRef(null);
  const [animPaused, setAnimPaused] = useState(false);
  const isVideo = isVideoMedia(item.image);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideo) return undefined;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      video.pause();
      setAnimPaused(true);
    }
    return undefined;
  }, [isVideo, item.image]);

  const onToggleAnimation = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
      setAnimPaused(false);
    } else {
      video.pause();
      setAnimPaused(true);
    }
  };

  const content = (
    <>
      <div className={`work-row__media${isVideo ? " work-row__media--video" : ""}`}>
        {isVideo ? (
          <>
            <video
              ref={videoRef}
              src={item.image}
              autoPlay
              muted
              loop
              playsInline
              aria-hidden="true"
              onPlay={() => setAnimPaused(false)}
              onPause={() => setAnimPaused(true)}
            />
            <button
              type="button"
              className={`work-row__anim-toggle${animPaused ? " work-row__anim-toggle--paused" : ""}`}
              onClick={onToggleAnimation}
              aria-pressed={animPaused}
              aria-label={animPaused ? "Play animation" : "Stop animation"}
            >
              {animPaused ? (
                <Play size={22} strokeWidth={2.25} aria-hidden="true" />
              ) : (
                <Pause size={22} strokeWidth={2.25} aria-hidden="true" />
              )}
            </button>
          </>
        ) : (
          <img src={item.image} alt="" />
        )}
      </div>
      <div className="work-row__copy">
        <h2 className="work-row__title">{item.title}</h2>
        <ul className="work-row__tags">
          {item.tags.map((tag) => (
            <li key={tag}>{tag}</li>
          ))}
        </ul>
        {item.metric ? <p className="work-row__metric">{item.metric}</p> : null}
      </div>
    </>
  );

  if (item.external) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noreferrer"
        className="work-row group"
      >
        {content}
      </a>
    );
  }

  return (
    <Link to={item.href} className="work-row group">
      {content}
    </Link>
  );
}

const Portfolio = () => {
  const { openDock, closeDock } = useAgentDock();
  const location = useLocation();
  const heroRef = useRef(null);
  const { domeImages } = useGlanceGallery();
  const [glanceOpen, setGlanceOpen] = useState(false);
  const preloadGlance = useCallback(() => {
    preloadGlanceTiles(domeImages, 12);
  }, [domeImages]);
  const [glanceImageOpen, setGlanceImageOpen] = useState(false);
  const [skipIntro] = useState(() => {
    try {
      return sessionStorage.getItem(INTRO_SEEN_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [intro, setIntro] = useState(skipIntro ? "done" : "in");
  const [heroDetailsVisible, setHeroDetailsVisible] = useState(skipIntro);
  const globalAnimationPaused = useGlobalAnimationPaused();

  useEffect(() => {
    if (!globalAnimationPaused) return undefined;
    setIntro("done");
    setHeroDetailsVisible(true);
    document.body.style.overflow = "";
    return undefined;
  }, [globalAnimationPaused]);

  useEffect(() => {
    if (skipIntro) return undefined;
    try {
      sessionStorage.setItem(INTRO_SEEN_KEY, "1");
    } catch {
      /* private mode */
    }
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const hold = reduced ? 400 : LOADER_MS;
    const fade = reduced ? 200 : 700;
    const out = window.setTimeout(() => setIntro("out"), hold);
    const done = window.setTimeout(() => setIntro("done"), hold + fade);
    document.body.style.overflow = "hidden";
    return () => {
      window.clearTimeout(out);
      window.clearTimeout(done);
      document.body.style.overflow = "";
    };
  }, [skipIntro]);

  useEffect(() => {
    if (intro === "done") document.body.style.overflow = "";
  }, [intro]);

  useLayoutEffect(() => {
    if (typeof window === "undefined" || intro !== "done") return;

    const mm = gsap.matchMedia();

    mm.add("(min-width: 768px)", () => {
      ScrollTrigger.create({
        trigger: "#hero",
        start: "top top",
        end: "+=100%",
        pin: true,
        pinSpacing: false,
        anticipatePin: 1,
      });

      const heroTitle = document.querySelector(".hero-title");
      const heroSub = document.querySelector(".hero-sub");
      const heroContent = document.querySelector(".hero-content");

      if (heroTitle && heroSub && heroContent) {
        gsap
          .timeline({
            scrollTrigger: {
              trigger: "#hero",
              start: "top top",
              end: "+=100%",
              scrub: 0.5,
            },
          })
          .to([heroTitle, heroSub], { opacity: 0.3, ease: "power2.inOut" }, 0)
          .to(heroContent, { filter: "blur(4px)", ease: "power2.inOut" }, 0)
          .to(heroContent, { scale: 0.95, ease: "power2.inOut" }, 0);
      }
    });

    return () => mm.revert();
  }, [intro]);

  useEffect(() => {
    const hash = location.hash;
    const id = hash === "#my-works" || hash === "#work" ? "my-works" : hash === "#writing" ? "writing" : null;
    if (id) {
      setTimeout(() => {
        const section = document.getElementById(id);
        if (section) {
          const headerOffset = 80;
          const offsetPosition = section.getBoundingClientRect().top + window.pageYOffset - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        }
      }, 100);
    }
  }, [location.hash]);

  useEffect(() => {
    if (!glanceOpen) {
      setGlanceImageOpen(false);
      return undefined;
    }
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (glanceImageOpen) return;
      setGlanceOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [glanceOpen, glanceImageOpen]);

  return (
    <>
      {intro !== "done" && (
        <div className={`home-loader${intro === "out" ? " is-out" : ""}`} aria-hidden="true">
          <AsciiTaiji />
        </div>
      )}
    <div
      className={`home-shell${intro !== "in" ? " is-in" : ""}${skipIntro ? " is-instant" : ""}${glanceOpen ? " is-glance-open" : ""}`}
    >
    <Layout>
      <div id="hero">
        <section ref={heroRef} className="portfolio-hero" aria-label="Hero">
          <div className="portfolio-hero__bg" aria-hidden="true" />
          <div className="portfolio-hero__shade" aria-hidden="true" />
          <div className="portfolio-hero__glow" aria-hidden="true" />
          <div className="portfolio-grain" aria-hidden="true" />
          {!glanceOpen && <SplashCursor />}
          <div className="hero-content">
            <div className="hero-copy">
              <GhostType
                as="h1"
                className="hero-title font-NeuePower-Regular text-white text-4xl md:text-5xl lg:text-5xl leading-[1.15]"
                text="I take ownership, build systems that scale,"
                completions={[" and deliver results that compound."]}
                accentColor="rgba(255, 255, 255, 0.85)"
                ghostOpacity={0.36}
                align="left"
                hint={false}
                loop={false}
                startOnView={false}
                paused={intro !== "done" && !globalAnimationPaused}
                instant={skipIntro || globalAnimationPaused}
                typeSpeed={38}
                thinkDelay={500}
                acceptDelay={1200}
                holdDelay={400}
                streamBy="word"
                onComplete={() => setHeroDetailsVisible(true)}
              />
              <FadeIn play={heroDetailsVisible} instant={skipIntro} forceVisible={globalAnimationPaused} delay={0} duration={0.65} direction="up" distance={14}>
                <div className="hero-sub hero-meta">
                  <p className="hero-meta__line">
                    Founding product designer at{" "}
                    <a href="https://collabstr.com/" target="_blank" rel="noreferrer">
                      Collabstr
                    </a>
                    {" · "}
                    Previously{" "}
                    <a href="https://intranetconnections.com/" target="_blank" rel="noreferrer">
                      Intranet Connections
                    </a>
                  </p>
                  <p className="hero-meta__line">
                    Mentor on{" "}
                    <a href="https://adplist.org/mentors/young-he" target="_blank" rel="noreferrer">
                      ADPList
                    </a>
                    {" · "}
                    Teacher at{" "}
                    <a href="https://builtyoung.org/" target="_blank" rel="noreferrer">
                      Built Young
                    </a>
                  </p>
                </div>
              </FadeIn>
              <FadeIn play={heroDetailsVisible} instant={skipIntro} forceVisible={globalAnimationPaused} delay={0.18} duration={0.65} direction="up" distance={14}>
                <div className="hero-actions">
                  <button type="button" className="hero-cta" onClick={openDock}>
                    Chat with Digital Young
                  </button>
                  <button
                    type="button"
                    className="hero-link"
                    onMouseEnter={preloadGlance}
                    onFocus={preloadGlance}
                    onClick={() => {
                      preloadGlance();
                      closeDock();
                      setGlanceOpen(true);
                    }}
                  >
                    Work at a glance
                  </button>
                </div>
              </FadeIn>
            </div>
            <FadeIn
              play={heroDetailsVisible}
              instant={skipIntro}
              forceVisible={globalAnimationPaused}
              delay={0.28}
              duration={0.7}
              direction="up"
              distance={18}
              className="hero-portrait-wrap"
            >
              <img src="/young-portrait.png" alt="Young He" className="hero-portrait" />
            </FadeIn>
          </div>
        </section>
      </div>

      {glanceOpen && (
        <div
          className="glance"
          role="dialog"
          aria-modal="true"
          aria-labelledby="glance-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setGlanceOpen(false);
          }}
        >
          <div className="glance__panel">
            <header className="glance__top">
              <h2 id="glance-title">Work at a glance</h2>
              <button
                type="button"
                className="glance__exit"
                onClick={() => setGlanceOpen(false)}
                aria-label="Exit gallery, Escape"
              >
                Exit gallery <span className="glance__exit-key">esc</span>
              </button>
            </header>
            <div className="glance__stage">
              <Suspense fallback={<div className="glance__stage-fallback" aria-hidden="true" />}>
                <DomeGallery
                  onEnlargedChange={setGlanceImageOpen}
                  images={domeImages}
                  grayscale={false}
                  overlayBlurColor="#050505"
                  fit={0.58}
                  minRadius={600}
                  maxVerticalRotationDeg={5}
                  dragSensitivity={16}
                  imageBorderRadius="12px"
                  openedImageBorderRadius="14px"
                  openedImageWidth="min(92vw, 960px)"
                  openedImageHeight="min(68vh, 620px)"
                />
              </Suspense>
            </div>
          </div>
        </div>
      )}

      <div id="next">
        <section id="my-works" className="work-section px-6 sm:px-10 md:px-16 lg:px-24 py-20 md:py-28" aria-label="Selected work">
          <p className="work-section__label">My works</p>
          <div className="work-grid">
            {WORK.map((item) => (
              <WorkRowCard key={item.href} item={item} />
            ))}
          </div>
        </section>

      <section id="writing" className="portfolio-panel text-white px-6 sm:px-10 md:px-16 lg:px-24 py-20 md:py-28" aria-label="Writing">
        <p className="text-white/45 text-xs tracking-[0.18em] uppercase mb-10">Writing</p>
        <div className="max-w-3xl space-y-12">
          {WRITINGS.map((article) => (
            <article key={article.href}>
              <p className="text-white/40 text-xs mb-2">
                {article.date} · {article.tags}
              </p>
              <h2 className="font-NeuePower-Regular text-2xl md:text-3xl leading-tight mb-2">{article.title}</h2>
              <p className="text-white/60 text-sm md:text-base leading-relaxed max-w-xl mb-4">{article.blurb}</p>
              <a
                href={article.href}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-white/80 hover:text-white underline underline-offset-4"
              >
                Medium →
              </a>
            </article>
          ))}
        </div>
        <SocialLinks className="page-socials" />
      </section>
      </div>
    </Layout>
    </div>
    </>
  );
};

export default Portfolio;
